import { useEffect } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';
import { useOrgModel } from './useOrgModel';
import { resolveBlockedLensDisplayFields } from '../services/orgAnalyticsLensFields';

/**
 * Applies an analytics lens named by `?lens=` to the live chart.
 *
 * Deliberately thin: the rules, the presets and the apply mechanism already
 * exist as the store's built-in full presets (`builtin-*`), and the AI preset
 * tool applies them with `loadFullPreset`. This does the same rather than
 * building a second way to colour the chart.
 *
 * What is genuinely new is *eligibility*. The store decides locally from
 * `requiredFields`, which answers "does this chart have a salary field?" — and
 * on a redacted chart that quietly becomes "no", which is right but accidental.
 * The permissioned org-model response answers it deliberately, per viewer, so
 * a preset a viewer has no grant for is never offered in the first place.
 */
const LENS_KEY_TO_BUILTIN_PRESET: Record<string, string> = {
  'span-of-control': 'builtin-span-of-control',
  'performance-distribution': 'builtin-performance',
  'compensation-bands': 'builtin-compensation',
  'tenure-distribution': 'builtin-tenure',
  'flight-risk': 'builtin-flight-risk',
  'succession-readiness': 'builtin-succession',
  'vacancy-highlight': 'builtin-vacancy',
};

export function useOrgAnalyticsLens(input: {
  diagramId: string | null | undefined;
  folderId: string | null | undefined;
  enabled: boolean;
}): void {
  const search = useSearch({ strict: false }) as { lens?: string } | undefined;
  const lensKey = typeof search?.lens === 'string' && search.lens.trim() ? search.lens : undefined;
  // Fetched whenever this is an Atlas chart, not only when a `?lens=` is
  // present: the answer also gates what a *persisted* default lens may show
  // this viewer, which matters on every open.
  const isAtlasChart = input.enabled && Boolean(input.folderId);
  // This has the same TanStack query key as usePeopleChartSync. The editor
  // therefore shares one permissioned model request instead of calling
  // /atlas/lenses and making the server rebuild the same model a second time.
  const orgModel = useOrgModel({
    folderId: input.folderId,
    diagramId: input.diagramId,
    enabled: isAtlasChart,
  });
  const eligible = orgModel.data?.lenses?.analytics;

  const setBlockedDisplayFields = useViewRulesStore((state) => state.setBlockedDisplayFields);
  useEffect(() => {
    if (!isAtlasChart) {
      setBlockedDisplayFields([]);
      return;
    }
    // Until the server answers, block every governed field. Over-blocking for
    // a moment costs a late-appearing legend; under-blocking discloses.
    setBlockedDisplayFields(resolveBlockedLensDisplayFields(eligible));
  }, [eligible, isAtlasChart, setBlockedDisplayFields]);
  // Both are set by the store's `initialize`, which binds it to this diagram and
  // populates the built-in presets. Subscribing to them is what makes this wait:
  // applying before initialize either finds no presets to apply, or gets undone
  // when initialize resets `rules` for a document that carries none.
  const fullPresets = useViewRulesStore((state) => state.fullPresets);
  const storeDiagramId = useViewRulesStore((state) => state.diagramId);

  useEffect(() => {
    if (!input.enabled || !lensKey || !eligible) return;
    if (!input.diagramId || storeDiagramId !== input.diagramId) return;
    // Server-side eligibility gates the lens: a viewer whose model is redacted
    // never gets the preset back, so the URL alone cannot summon it.
    if (!eligible.some((preset) => preset.key === lensKey)) return;
    const presetId = LENS_KEY_TO_BUILTIN_PRESET[lensKey];
    if (!presetId) return;
    if (!fullPresets.some((preset) => preset.id === presetId)) return;

    useViewRulesStore.getState().loadFullPreset(presetId, { persist: false });
  }, [eligible, fullPresets, input.diagramId, input.enabled, lensKey, storeDiagramId]);
}
